import * as Yup from "yup";
import { SingleButtonSelectorField } from "src/components/FormField/SingleButtonSelectorField";
import { TextField } from "src/components/FormField/TextField";
import { MinMaxNumberField } from "src/components/FormField/MinMaxNumberField";
import { SingleSelectField } from "src/components/FormField/SingleSelectField";
import { EditorField } from "src/components/FormField/EditorField";
import {
  GREATER_THAN_ZERO_VALIDATION,
  MIN_MAX_FIELDS_VALIDATION,
} from "src/constants/formValidation";
import { inquirySettingsTrns } from "./trns";
import { IRenderFormField, ISelectOption } from "src/@types/form";
import { InquiryFields } from "./types";

const DEFAULT_TEXT_FIELD_ERROR_CLASSNAME = "absolute -bottom-5";
const DEFAULT_SINGLE_BUTTON_SELECTOR_ERROR_CLASSNAME = "text-xs bottom-px";

const COMPANY_TURNOVER_VALIDATION_SCHEMA = MIN_MAX_FIELDS_VALIDATION(
  InquiryFields.inquiryTurnoverMin,
  InquiryFields.inquiryTurnoverMax
);
const MONTHLY_INCOME_VALIDATION_SCHEMA = MIN_MAX_FIELDS_VALIDATION(
  InquiryFields.monthlyIncomeMin,
  InquiryFields.monthlyIncomeMax
);

const EMPLOYEE_COUNT_OPTIONS = [
  {
    id: "<10",
    label: "<10",
    value: "<10",
  },
  {
    id: "10-50",
    label: "10-50",
    value: "10-50",
  },
  {
    id: ">50",
    label: ">50",
    value: ">50",
  },
];

const HOW_LONG_YEAR_OPTIONS = [
  {
    value: "<2",
    id: "less than 2",
    label: "<2",
  },
  {
    value: "2-5",
    id: "between 3 and 5",
    label: "2-5",
  },
  {
    value: ">5",
    id: "more than 5",
    label: ">5",
  },
];

const CUSTOMERS_COUNT_OPTIONS = [
  {
    value: "<1000",
    id: "<1000",
    label: "1000<",
  },
  {
    value: ">1000",
    id: ">1000",
    label: "1000>",
  },
];

export const INQUIRY_SETTINGS_VALIDATION_SCHEMA = Yup.object().shape({
  [InquiryFields.inquiryType]: Yup.string().required("required"),
  [InquiryFields.title]: Yup.string().required("required"),
  [InquiryFields.industry]: Yup.string().required("required"),
  [InquiryFields.product]: Yup.string().required("required"),
  [InquiryFields.employees]: Yup.string().required("required"),
  [InquiryFields.yearsOnMarket]: Yup.string().required("required"),
  [InquiryFields.customersCount]: Yup.string().required("required"),
  [InquiryFields.inquiryTurnoverMin]: COMPANY_TURNOVER_VALIDATION_SCHEMA.min,
  [InquiryFields.inquiryTurnoverMax]: COMPANY_TURNOVER_VALIDATION_SCHEMA.max,
  [InquiryFields.monthlyIncomeMin]: MONTHLY_INCOME_VALIDATION_SCHEMA.min,
  [InquiryFields.monthlyIncomeMax]: MONTHLY_INCOME_VALIDATION_SCHEMA.max,
  [InquiryFields.investmentForStart]: GREATER_THAN_ZERO_VALIDATION,
  [InquiryFields.inquiryCost]: GREATER_THAN_ZERO_VALIDATION,
});

export const getinquiryEditCreateFields = (
  inquiryTypeOptions: ISelectOption[],
  industryOptions: ISelectOption[]
): IRenderFormField[] => [
  {
    name: InquiryFields.inquiryType,
    className: "flex flex-col sm:flex-row mt-5 xl:mt-10",
    errorClassName: "text-xs bottom-5",
    options: inquiryTypeOptions,
    component: SingleButtonSelectorField,
    selectorClassName: "!justify-start px-5 py-3.5",
  },
  {
    name: InquiryFields.title,
    errorClassName: DEFAULT_TEXT_FIELD_ERROR_CLASSNAME,
    label: {
      i18nKey: inquirySettingsTrns.titleField,
    },
    component: TextField,
  },
  {
    name: InquiryFields.industry,
    errorClassName: DEFAULT_TEXT_FIELD_ERROR_CLASSNAME,
    label: {
      i18nKey: inquirySettingsTrns.industry,
    },
    component: SingleSelectField,
    options: industryOptions,
  },
  {
    name: InquiryFields.product,
    errorClassName: DEFAULT_TEXT_FIELD_ERROR_CLASSNAME,
    label: {
      i18nKey: inquirySettingsTrns.product,
    },
    component: TextField,
  },
  {
    component: SingleButtonSelectorField,
    name: InquiryFields.employees,
    label: { i18nKey: inquirySettingsTrns.employees },
    className: "flex-1 justify-end !gap-2 w-full sm:!gap-4",
    fieldClassName: "flex sm:!flex-row sm:items-center justify-between",
    selectorClassName: "max-w-67 w-full px-5 py-3",
    options: EMPLOYEE_COUNT_OPTIONS,
    errorClassName: DEFAULT_TEXT_FIELD_ERROR_CLASSNAME,
  },
  {
    name: InquiryFields.yearsOnMarket,
    className: "flex-1 justify-end !gap-2 w-full sm:!gap-4",
    fieldClassName: "flex sm:!flex-row sm:items-center justify-between",
    errorClassName: DEFAULT_SINGLE_BUTTON_SELECTOR_ERROR_CLASSNAME,
    selectorClassName: "max-w-64 w-full px-5 py-3",
    label: {
      i18nKey: inquirySettingsTrns.yearsOnTheMarket,
    },
    options: HOW_LONG_YEAR_OPTIONS,
    component: SingleButtonSelectorField,
  },
  {
    name: InquiryFields.customersCount,
    className: "flex-1 justify-end !gap-2 w-full sm:!gap-4",
    fieldClassName: "flex sm:!flex-row sm:items-center justify-between",
    selectorClassName: "max-w-64 w-full px-5 py-3",
    label: {
      i18nKey: inquirySettingsTrns.customerCount,
    },
    options: CUSTOMERS_COUNT_OPTIONS,
    component: SingleButtonSelectorField,
  },
  {
    name: [InquiryFields.inquiryTurnoverMin, InquiryFields.inquiryTurnoverMax],
    errorClassName: DEFAULT_TEXT_FIELD_ERROR_CLASSNAME,
    label: {
      i18nKey: inquirySettingsTrns.annualTurnover,
    },
    labelClassName: "flex flex-1 w-full capitalize !text-wrap",
    fieldClassName:
      "flex !flex-col !justify-start sm:!flex-row gap-5 sm:items-center",
    component: MinMaxNumberField,
  },
  {
    name: [InquiryFields.monthlyIncomeMin, InquiryFields.monthlyIncomeMax],
    errorClassName: DEFAULT_TEXT_FIELD_ERROR_CLASSNAME,
    label: {
      i18nKey: inquirySettingsTrns.monthlyIncome,
    },
    labelClassName: "flex flex-1 w-full capitalize !text-wrap",
    fieldClassName:
      "flex !flex-col !justify-start sm:!flex-row gap-5 sm:items-center",
    component: MinMaxNumberField,
  },
  {
    name: InquiryFields.investmentForStart,
    errorClassName: DEFAULT_TEXT_FIELD_ERROR_CLASSNAME,
    type: "number",
    label: {
      i18nKey: inquirySettingsTrns.minInvestment,
    },
    component: TextField,
  },
  {
    name: InquiryFields.inquiryCost,
    errorClassName: DEFAULT_TEXT_FIELD_ERROR_CLASSNAME,
    type: "number",

    label: {
      i18nKey: inquirySettingsTrns.listPrice,
    },
    component: TextField,
  },
  {
    name: InquiryFields.additionalInformation,
    label: {
      i18nKey: inquirySettingsTrns.description,
    },
    component: EditorField,
  },
];
